import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import { FiAlertCircle, FiRefreshCw, FiPieChart, FiTrendingUp, FiPlus, FiUser, FiCalendar, FiArrowRight, FiEye, FiEyeOff } from 'react-icons/fi';
import { Line, Pie } from 'react-chartjs-2';
import { ConfigProvider, theme } from 'antd';
import { 
  Chart as ChartJS, 
  LineElement, 
  PointElement, 
  CategoryScale, 
  LinearScale, 
  ArcElement, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import api from '../../utils/api';
import './dashboard.css';
// Import for pending actions popup
import { Modal, Table, Tag, Button, Space, Tabs, Badge, Alert, Typography, Tooltip as AntTooltip } from 'antd';
import { 
  ClockCircleOutlined, 
  SignatureOutlined, 
  EyeOutlined, 
  WarningOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  FileWordOutlined
} from '@ant-design/icons';
import moment from 'moment';
const { Text } = Typography;

// Register chart.js components
ChartJS.register(
  LineElement, 
  PointElement, 
  CategoryScale, 
  LinearScale, 
  ArcElement, 
  Tooltip, 
  Legend,
  Filler
);

const Dashboard = () => {
    const { user, isAuthenticated, loading: authLoading, error: authError } = useAuth();
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeView, setActiveView] = useState('all-tickets');
    const [chartView, setChartView] = useState('all-tickets');
    const [pieChartMode, setPieChartMode] = useState('current-month');
    const [isDark, setIsDark] = useState(document.body.classList.contains('dark-mode'));
    const [showOverviewSection, setShowOverviewSection] = useState(true);
    const [showBreakdownSection, setShowBreakdownSection] = useState(true);
    const [showAnalyticsSection, setShowAnalyticsSection] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const notificationApi = useNotificationApi();
    // Pending actions popup states
    const [showPendingPopup, setShowPendingPopup] = useState(false);
    const [inProgressTickets, setInProgressTickets] = useState([]);
    const [pendingSignatureTickets, setPendingSignatureTickets] = useState([]);
    const popupShownRef = useRef(false);  // ← ADD THIS LINE

    // Check screen size
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Unknown';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            console.error('Date formatting error:', e, dateString);
            return 'Unknown';
        }
    };

    // Format aging display for popup
    const getAgingDisplay = (days, hours) => {
        if (days === null || days === undefined) return 'N/A';
        if (days === 0) {
            if (hours && hours < 24) {
                const hrs = Math.floor(hours);
                return hrs === 0 ? '< 1 hour' : `${hrs} hour${hrs > 1 ? 's' : ''}`;
            }
            return 'Today';
        }
        if (days === 1) return '1 day';
        return `${days}D`;
    };

    const getAgingColor = (days) => {
        if (days >= 7) return 'red';
        if (days >= 3) return 'orange';
        if (days >= 1) return 'gold';
        return 'green';
    };

const fetchPendingActions = useCallback(async () => {
    try {
        // Check if popup has been shown this session
        const popupShown = sessionStorage.getItem('pendingActionsPopupShown') === 'true';
        if (popupShown) {
            return; // Don't fetch if already shown
        }
        
        const response = await api.get('/api/pending-actions');
        if (response.data.success && response.data.data.hasActions) {
            let allPendingTickets = response.data.data.pendingSignatureTickets || [];
            const userId = user?.id;
            const userRoleId = user?.roleId;
            
            let processedTickets = [];
            
            // For System Admin, add requires_my_signature flag
            if (userRoleId === 1) {
                processedTickets = allPendingTickets.map(ticket => ({
                    ...ticket,
                    requires_my_signature: ticket.signature_type === 'supervisor_signature' && ticket.my_supervisor_id === userId
                }));
            } else {
                processedTickets = allPendingTickets;
            }
            
            setInProgressTickets(response.data.data.inProgressTickets || []);
            setPendingSignatureTickets(processedTickets);
            setTimeout(() => {
                setShowPendingPopup(true);
                sessionStorage.setItem('pendingActionsPopupShown', 'true');
            }, 100);
        } else {
            sessionStorage.setItem('pendingActionsPopupShown', 'true');
        }
    } catch (error) {
        console.error('Error fetching pending actions:', error);
    }
}, [user]);

    // Handle popup actions
    const handleViewTicketFromPopup = (ticketId) => {
        setShowPendingPopup(false);
        navigate(`/tickets/${ticketId}`);
    };

    const handleSignTicketFromPopup = (ticketId, signatureType) => {
        setShowPendingPopup(false);
        navigate(`/tickets/${ticketId}`, { state: { openSignModal: true, signatureType } });
    };

    // Handle theme changes
    useEffect(() => {
        const handleThemeChange = (mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    const newIsDark = document.body.classList.contains('dark-mode');
                    setIsDark(newIsDark);
                }
            });
        };

        const observer = new MutationObserver(handleThemeChange);
        observer.observe(document.body, { 
            attributes: true, 
            attributeFilter: ['class'] 
        });

        return () => observer.disconnect();
    }, []);

    // Handle authentication state
    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/login');
        }
    }, [isAuthenticated, authLoading, navigate]);

    // Notification helpers
    const showError = (title, content, key = 'dashboardError') => {
        notificationApi.error({
            key,
            message: title,
            description: content,
            duration: 4.5,
            placement: 'topRight',
            style: { width: 400, zIndex: 10000 },
        });
    };

    const showSuccess = (title, description, key = 'dashboardSuccess') => {
        notificationApi.success({
            key,
            message: title,
            description,
            duration: 3,
            placement: 'topRight',
            style: { width: 400, zIndex: 10000 },
        });
    };

    const showLoading = (key, messageContent) => {
        let dismissNotification;
        const delay = 300;
        const minDisplayTime = 500;
        const startTime = Date.now();

        const timer = setTimeout(() => {
            dismissNotification = notificationApi.open({
                key,
                message: messageContent,
                duration: 0,
                placement: 'topRight',
                style: { width: 400, zIndex: 10000 },
            });
        }, delay);

        return () => {
            clearTimeout(timer);
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, minDisplayTime - elapsed);
            setTimeout(() => {
                if (dismissNotification) {
                    typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
                } else {
                    notificationApi.destroy(key);
                }
            }, remaining);
        };
    };

    // Fetch dashboard data
    const fetchDashboardData = useCallback(async () => {
        if (!isAuthenticated || authLoading || !user) return;

        setIsLoading(true);
        setError(null);
        const dismissLoading = showLoading('fetchDashboard', 'Loading dashboard data...');

        try {
            const response = await api.get('api/dashboard/summary');
            if (response.data.success) {
                const data = response.data.data;
                console.log('API response:', data);
                setDashboardData(data);
                showSuccess('Dashboard Loaded', 'Successfully fetched dashboard data.');
            } else {
                throw new Error(response.data.message || 'Failed to load dashboard data');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            const errorMessage = err.response?.data?.message || 'Failed to load dashboard data';
            showError('Loading Error', errorMessage, 'fetchDashboardError');
            setError(errorMessage);
        } finally {
            setIsLoading(false);
            dismissLoading();
        }
    }, [user, isAuthenticated, authLoading, notificationApi]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

useEffect(() => {
    const popupShown = sessionStorage.getItem('pendingActionsPopupShown') === 'true';
    
    if (dashboardData && !popupShown) {
        const timer = setTimeout(() => {
            fetchPendingActions();
        }, 300);
        return () => clearTimeout(timer);
    }
}, [dashboardData]);

    // Refresh data handler
    const handleRefresh = () => {
        fetchDashboardData();
    };

    // Create new ticket handler
    const handleCreateTicket = () => {
        navigate('/tickets/new');
    };

    // Navigate to ticket by ID
    const handleTicketClick = (ticketUuid) => {
        const allRecentTickets = [
            ...(dashboardData?.recentTickets || []),
            ...(dashboardData?.recentMyTickets || [])
        ];
        
        const ticket = allRecentTickets.find(t => t.ticketUuid === ticketUuid);
        if (ticket?.id) {
            navigate(`/tickets/${ticket.id}`);
        } else {
            console.error('Ticket ID not found for UUID:', ticketUuid);
            showError('Error', 'Could not find ticket details. Please try again.');
        }
    };

    // Navigate to filtered tickets
    const handleCardClick = (filterType) => {
        setActiveView(filterType);
        setChartView(filterType);
        let route = '/tickets';
        if (filterType === 'my-tickets') {
            route = '/my-tickets';
        }
        navigate(route);
    };

    // Switch chart view
    const handleChartViewChange = (viewType) => {
        setChartView(viewType);
    };

    // Toggle pie chart mode
    const handlePieChartModeChange = (mode) => {
        setPieChartMode(mode);
    };

    // Toggle section visibility
    const toggleOverviewSection = () => {
        setShowOverviewSection(prev => !prev);
    };

    const toggleBreakdownSection = () => {
        setShowBreakdownSection(prev => !prev);
    };

    const toggleAnalyticsSection = () => {
        setShowAnalyticsSection(prev => !prev);
    };

    // Get the correct status counts based on active view
    const getStatusCountsForView = () => {
        if (!dashboardData) return {};
        return {
            'all-tickets': dashboardData.statusCounts || {},
            'my-tickets': dashboardData.myTicketsStatusCounts || {}
        }[activeView] || {};
    };

    // Get the correct chart data
    const getChartDataForView = () => {
        if (!dashboardData) {
            return { monthlyVolume: {}, currentMonthStatus: {}, allTimeStatus: {} };
        }
        
        const chartData = {
            monthlyVolume: {},
            currentMonthStatus: {},
            allTimeStatus: {}
        };

        switch(chartView) {
            case 'my-tickets':
                chartData.monthlyVolume = dashboardData.monthlyVolumeMy || {};
                chartData.currentMonthStatus = dashboardData.currentMonthStatusMy || {};
                chartData.allTimeStatus = dashboardData.myTicketsStatusCounts || {};
                break;
            case 'all-tickets':
            default:
                chartData.monthlyVolume = dashboardData.monthlyVolume || {};
                chartData.currentMonthStatus = dashboardData.currentMonthStatus || {};
                chartData.allTimeStatus = dashboardData.statusCounts || {};
                break;
        }

        return chartData;
    };

    // Get recent tickets
    const getRecentTickets = () => {
        if (!dashboardData || !user) return [];
        return {
            'all-tickets': dashboardData.recentTickets || [],
            'my-tickets': dashboardData.recentMyTickets || []
        }[chartView] || [];
    };

    // Status colors
    const statusColorMap = {
        'Unassigned': '#040403ff',
        'Waiting For Support': '#f97316',
        'In Progress': '#3b82f6',
        'Waiting For Customer': '#8b5cf6',
        'Escalated': '#ef4444',
        'Resolved': '#10b981',
        'Closed': '#35b910ff',
        'Cancelled': '#d1d5db',
        'Reopened': '#f59e0b',
        'Unknown': '#6b7280'
    };

    // Get computed CSS variable value
    const getCssVariable = (variableName) => {
        return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
    };

    // Prepare pie chart data
    const preparePieChartData = () => {
        const chartData = getChartDataForView();
        const statusData = pieChartMode === 'all-time' ? chartData.allTimeStatus : chartData.currentMonthStatus;
        const labels = Object.keys(statusData);
        const data = labels.map(status => statusData[status] || 0);
        const backgroundColors = labels.map(status => statusColorMap[status] || '#6b7280');

        const totalTickets = data.reduce((sum, val) => sum + val, 0);
        if (totalTickets === 0) {
            return {
                labels: ['No Tickets'],
                data: [1],
                backgroundColors: ['#d1d5db']
            };
        }

        return { labels, data, backgroundColors };
    };

    // Loading state
    if (authLoading || isLoading) {
        return (
            <ConfigProvider theme={{ algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
                <div className="dashboard-loading">
                    <FiRefreshCw className="animate-spin text-2xl text-primary" />
                    <p>Loading dashboard...</p>
                </div>
            </ConfigProvider>
        );
    }

    // Error state
    if (authError || error || !user) {
        const message = authError || error || 'User information not available';
        return (
            <ConfigProvider theme={{ algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
                <div className="dashboard-error">
                    <FiAlertCircle className="text-danger text-2xl" />
                    <h3>{authError ? 'Authentication Error' : 'Dashboard Error'}</h3>
                    <p>{message}</p>
                    <button onClick={() => window.location.reload()}>
                        <FiRefreshCw className="mr-2" />
                        Retry
                    </button>
                </div>
            </ConfigProvider>
        );
    }

    const roleName = user?.roleName || user?.role?.name || 'User';
    const userName = user?.firstName || user?.name || 'User';
    const statusCounts = getStatusCountsForView();
    const chartData = getChartDataForView();
    const recentTickets = getRecentTickets();
    const pieChartData = preparePieChartData();
    const currentMonthYear = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

    // Card data structure
    const cardTiers = [
        [
            { 
                title: 'Total Tickets', 
                value: dashboardData?.totalTickets || 0, 
                type: 'all-tickets',
                color: '#6b7280',
                size: 'large',
                isActive: activeView === 'all-tickets'
            },
            { 
                title: 'My Tickets', 
                value: dashboardData?.myTicketsCount || 0, 
                type: 'my-tickets',
                color: '#3b82f6',
                size: 'large',
                isActive: activeView === 'my-tickets'
            }
        ],
        [
            { 
                title: 'Unassigned', 
                value: statusCounts['Unassigned'] || 0, 
                type: 'unassigned',
                color: statusColorMap['Unassigned'],
                size: 'medium'
            },
            { 
                title: 'Waiting For Support', 
                value: statusCounts['Waiting For Support'] || 0, 
                type: 'waiting-for-support',
                color: statusColorMap['Waiting For Support'],
                size: 'medium'
            },
            { 
                title: 'In Progress', 
                value: statusCounts['In Progress'] || 0, 
                type: 'in-progress',
                color: statusColorMap['In Progress'],
                size: 'medium'
            },
            { 
                title: 'Waiting For Customer', 
                value: statusCounts['Waiting For Customer'] || 0, 
                type: 'waiting-for-customer',
                color: statusColorMap['Waiting For Customer'],
                size: 'medium'
            }
        ],
        [
            { 
                title: 'Escalated', 
                value: statusCounts['Escalated'] || 0, 
                type: 'escalated',
                color: statusColorMap['Escalated'],
                size: 'small'
            },
            { 
                title: 'Resolved', 
                value: statusCounts['Resolved'] || 0, 
                type: 'resolved',
                color: statusColorMap['Resolved'],
                size: 'small'
            },
            { 
                title: 'Closed', 
                value: statusCounts['Closed'] || 0, 
                type: 'closed',
                color: statusColorMap['Closed'],
                size: 'small'
            },
            { 
                title: 'Cancelled', 
                value: statusCounts['Cancelled'] || 0, 
                type: 'cancelled',
                color: statusColorMap['Cancelled'],
                size: 'small'
            },
            { 
                title: 'Reopened', 
                value: statusCounts['Reopened'] || 0, 
                type: 'reopened',
                color: statusColorMap['Reopened'],
                size: 'small'
            }
        ]
    ];

    // Line chart data
    const lineChartData = {
        labels: Object.keys(chartData.monthlyVolume).length > 0 
            ? Object.keys(chartData.monthlyVolume).sort()
            : ['No Data'],
        datasets: [{
            label: 'Ticket Volume',
            data: Object.keys(chartData.monthlyVolume).length > 0
                ? Object.values(chartData.monthlyVolume)
                : [0],
            borderColor: getCssVariable('--primary'),
            backgroundColor: (context) => {
                if (!context.chart.chartArea) return;
                const ctx = context.chart.ctx;
                const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                const primaryColor = getCssVariable('--primary');
                gradient.addColorStop(0, `${primaryColor}66`);
                gradient.addColorStop(1, `${primaryColor}1a`);
                return gradient;
            },
            fill: true,
            tension: 0.4,
            pointBackgroundColor: getCssVariable('--primary'),
            pointBorderColor: '#fff',
            pointRadius: 4,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: getCssVariable('--primary'),
            pointBorderWidth: 2
        }]
    };

    // Pie chart data
    const pieChartDataConfig = {
        labels: pieChartData.labels,
        datasets: [{
            data: pieChartData.data,
            backgroundColor: pieChartData.backgroundColors,
            borderColor: getCssVariable('--card-bg'),
            borderWidth: 2,
            hoverOffset: 12
        }]
    };

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { 
            position: isMobile ? 'bottom' : 'top', // Bottom on mobile to save space
            labels: {
                color: getCssVariable('--text-primary'),
                font: { size: isMobile ? 9 : 12 }, // Smaller on mobile
                boxWidth: isMobile ? 12 : 16, // Smaller legend boxes
                padding: isMobile ? 10 : 15 // Less padding
            }
        },
        tooltip: {
            backgroundColor: getCssVariable('--card-bg'),
            titleColor: getCssVariable('--text-primary'),
            bodyColor: getCssVariable('--text-primary'),
            borderColor: getCssVariable('--card-border'),
            borderWidth: 1,
            padding: isMobile ? 8 : 12, // Smaller on mobile
            boxPadding: isMobile ? 4 : 6
        }
    },
    scales: {
        y: {
            beginAtZero: true,
            grid: { 
                color: getCssVariable('--chart-grid'),
                drawBorder: false,
                display: isMobile ? false : true // Simplify grid on mobile
            },
            ticks: { 
                color: getCssVariable('--chart-text'),
                font: { size: isMobile ? 9 : 11 }, // Smaller on mobile
                stepSize: 1,
                precision: 0
            }
        },
        x: {
            grid: { 
                color: getCssVariable('--chart-grid'),
                drawBorder: false,
                display: isMobile ? false : true // Simplify grid
            },
            ticks: { 
                color: getCssVariable('--chart-text'),
                font: { size: isMobile ? 9 : 11 },
                maxRotation: isMobile ? 45 : 0, // Rotate to fit
                minRotation: isMobile ? 45 : 0
            }
        }
    },
    layout: {
        padding: isMobile ? 10 : 20 // Add padding for mobile
    },
    animation: {
        duration: isMobile ? 500 : 1000, // Faster on mobile
        easing: 'easeOutQuart'
    }
};

const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { 
            position: isMobile ? 'bottom' : 'right',
            labels: {
                color: getCssVariable('--text-primary'),
                padding: isMobile ? 10 : 15, // Less padding
                font: { size: isMobile ? 9 : 11 }, // Smaller on mobile
                boxWidth: isMobile ? 12 : 16, // Smaller boxes
                generateLabels: function(chart) {
                    const data = chart.data;
                    if (data.labels.length && data.datasets.length) {
                        return data.labels.map((label, i) => {
                            const value = data.datasets[0].data[i];
                            const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                            return {
                                text: isMobile ? `${label}: ${percentage}%` : `${label}: ${value} (${percentage}%)`, // Compact labels on mobile
                                fillStyle: data.datasets[0].backgroundColor[i],
                                strokeStyle: data.datasets[0].borderColor,
                                lineWidth: data.datasets[0].borderWidth,
                                hidden: isNaN(value) || value === 0,
                                index: i
                            };
                        });
                    }
                    return [];
                }
            }
        },
        tooltip: {
            backgroundColor: getCssVariable('--card-bg'),
            titleColor: getCssVariable('--text-primary'),
            bodyColor: getCssVariable('--text-primary'),
            borderColor: getCssVariable('--card-border'),
            borderWidth: 1,
            padding: isMobile ? 8 : 12,
            boxPadding: isMobile ? 4 : 6,
            callbacks: {
                label: function(context) {
                    const label = context.label || '';
                    const value = context.parsed || 0;
                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                    return isMobile ? `${label}: ${percentage}%` : `${label}: ${value} (${percentage}%)`; // Compact on mobile
                }
            }
        }
    },
    layout: {
        padding: isMobile ? 10 : 20 // Add padding
    },
    animation: {
        duration: isMobile ? 500 : 1000,
        easing: 'easeOutQuart'
    }
};

// Popup table columns for My Workload tickets (formerly In Progress)
// Popup table columns for My Workload tickets (formerly In Progress)
const inProgressPopupColumns = [
    {
        title: 'Ticket Number',
        dataIndex: 'ticket_uuid',
        key: 'ticket_uuid',
        width: 160,
        fixed: 'left',
        render: (text, record) => {
            if (!text) return 'N/A';
            return (
                <Button 
                    type="link" 
                    onClick={() => handleViewTicketFromPopup(record.id)} 
                    style={{ padding: 0, fontWeight: 600, color: '#1890ff', whiteSpace: 'normal', wordBreak: 'break-word', height: 'auto', textAlign: 'left' }}
                >
                    {text}
                </Button>
            );
        },
    },
    {
        title: 'Summary Title',
        dataIndex: 'title',
        key: 'title',
        width: 220,
        ellipsis: false,
        render: (text) => {
            if (!text) return 'N/A';
            return (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>
                    {text}
                </div>
            );
        },
    },
    // ============================================================
    // ADD CONTRACT COLUMN HERE
    // ============================================================
    {
        title: 'Contract',
        dataIndex: 'contract_name',
        key: 'contract_name',
        width: 160,
        render: (text) => {
            if (!text) return 'N/A';
            return (
                <AntTooltip title={text} placement="topLeft">
                    <div style={{ 
                        whiteSpace: 'normal', 
                        wordBreak: 'break-word', 
                        lineHeight: '1.4',
                        maxWidth: '100%'
                    }}>
                        {text}
                    </div>
                </AntTooltip>
            );
        },
    },
    // ============================================================
    {
        title: 'Created Aging',
        key: 'created_aging',
        width: 100,
        render: (_, record) => {
            const days = record.days_since_created;
            const color = getAgingColor(days);
            let display = getAgingDisplay(days, null);
            const tooltipText = `Created: ${moment(record.created_at).format('YYYY-MM-DD HH:mm:ss')}`;
            
            if (display !== 'N/A' && display !== '< 1 hour' && !display.includes('Today')) {
                display = `${display} AGO`;
            }
            
            return (
                <AntTooltip title={tooltipText}>
                    <Tag color={color} icon={<ClockCircleOutlined />} style={{ whiteSpace: 'nowrap' }}>
                        {display}
                    </Tag>
                </AntTooltip>
            );
        },
    },
    {
        title: 'Assigned Aging',
        key: 'assigned_aging',
        width: 100,
        render: (_, record) => {
            const days = record.days_since_assigned;
            const color = getAgingColor(days);
            let display = getAgingDisplay(days, null);
            const tooltipText = `Assigned: ${record.assigned_at ? moment(record.assigned_at).format('YYYY-MM-DD HH:mm:ss') : 'Not assigned yet'}`;
            
            if (display !== 'N/A' && display !== '< 1 hour' && !display.includes('Today') && record.assigned_at) {
                display = `${display} AGO`;
            }
            
            return (
                <AntTooltip title={tooltipText}>
                    <Tag color={color} icon={<UserOutlined />} style={{ whiteSpace: 'nowrap' }}>
                        {display}
                    </Tag>
                </AntTooltip>
            );
        },
    },
    {
        title: 'Status',
        dataIndex: 'status_name',
        key: 'status_name',
        width: 120,
        render: (status) => {
            const statusColorMapLocal = {
                'In Progress': '#3b82f6',
                'Waiting For Support': '#f97316',
                'Waiting For Customer': '#8b5cf6',
                'Escalated': '#ef4444',
                'Resolved': '#10b981',
                'Closed': '#2b814bff',
                'Unassigned': '#6b7280',
                'Reopened': '#f59e0b',
                'Cancelled': '#d1d5db'
            };
            const color = statusColorMapLocal[status] || '#6b7280';
            return (
                <Tag color={color} style={{ whiteSpace: 'nowrap' }}>
                    {status || 'Unknown'}
                </Tag>
            );
        },
    },
    {
        title: 'Assigned To',
        dataIndex: 'assigned_to_name',
        key: 'assigned_to_name',
        width: 130,
        ellipsis: true,
        render: (text) => {
            if (!text) return 'Unassigned';
            return (
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={text}>
                    {text}
                </div>
            );
        },
    },
    {
        title: 'Action',
        key: 'action',
        width: 75,
        fixed: 'right',
        render: (_, record) => (
            <Button 
                type="primary" 
                size="small" 
                icon={<EyeOutlined />}
                onClick={() => handleViewTicketFromPopup(record.id)}
                style={{ fontSize: '12px' }}
            >
                View
            </Button>
        ),
    },
];

const pendingSignatureSummaryColumns = [
    {
        title: 'Ticket Number',
        dataIndex: 'ticket_uuid',
        key: 'ticket_uuid',
        width: 170,
        render: (text, record) => (
            <Button 
                type="link" 
                onClick={() => {
                    setShowPendingPopup(false);
                    navigate(`/tickets/${record.id}`);
                }} 
                style={{ padding: 0, fontWeight: 600, color: '#1890ff', whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'left' }}
            >
                {text}
            </Button>
        ),
    },
    {
        title: 'Summary Title',
        dataIndex: 'title',
        key: 'title',
        width: 250,
        render: (text) => {
            if (!text) return 'N/A';
            return (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>
                    {text}
                </div>
            );
        },
    },
    // ============================================================
    // ADD CONTRACT COLUMN HERE
    // ============================================================
    {
        title: 'Contract',
        dataIndex: 'contract_name',
        key: 'contract_name',
        width: 160,
        render: (text) => {
            if (!text) return 'N/A';
            return (
                <AntTooltip title={text} placement="topLeft">
                    <div style={{ 
                        whiteSpace: 'normal', 
                        wordBreak: 'break-word', 
                        lineHeight: '1.4',
                        maxWidth: '100%'
                    }}>
                        {text}
                    </div>
                </AntTooltip>
            );
        },
    },
    // ============================================================
    {
        title: 'Pending Signature Type',
        dataIndex: 'signature_type',
        key: 'signature_type',
        width: 200,
        render: (type) => {
            const typeMap = {
                'supervisor_signature': { 
                    text: '🏢 Internal Supervisor Signature', 
                    color: 'blue',
                    description: 'Waiting for Internal/Partner Supervisor to sign'
                },
                'customer_standard_signature': { 
                    text: '👤 Customer Standard Signature', 
                    color: 'orange',
                    description: 'Waiting for Customer Standard user to sign'
                },
                'customer_supervisor_signature': { 
                    text: '👥 Customer Supervisor Signature', 
                    color: 'purple',
                    description: 'Waiting for Customer Supervisor to sign'
                }
            };
            const info = typeMap[type] || { text: type || 'Unknown', color: 'default', description: '' };
            return (
                <AntTooltip title={info.description}>
                    <Tag color={info.color} icon={<SignatureOutlined />} style={{ whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'center' }}>
                        {info.text}
                    </Tag>
                </AntTooltip>
            );
        }
    },
    {
        title: 'Related To',
        key: 'user_info',
        width: 140,
        ellipsis: true,
        render: (_, record) => {
            const name = record.assigned_to_name || record.created_by_name || 'N/A';
            return (
                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={name}>
                    {name}
                </div>
            );
        }
    },
];

// Popup table columns for Pending Signature tickets
const pendingSignaturePopupColumns = [
    {
        title: 'Ticket Number',
        dataIndex: 'ticket_uuid',
        key: 'ticket_uuid',
        width: 160,
        fixed: 'left',
        render: (text, record) => {
            if (!text) return 'N/A';
            return (
                <Button 
                    type="link" 
                    onClick={() => handleViewTicketFromPopup(record.id)} 
                    style={{ padding: 0, fontWeight: 600, color: '#1890ff', whiteSpace: 'normal', wordBreak: 'break-word', height: 'auto' }}
                >
                    {text}
                </Button>
            );
        },
    },
    {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        width: 220,
        ellipsis: false,
        render: (text) => {
            if (!text) return 'N/A';
            return (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.4' }}>
                    {text}
                </div>
            );
        },
    },
    // ============================================================
    // ADD CONTRACT COLUMN HERE
    // ============================================================
    {
        title: 'Contract',
        dataIndex: 'contract_name',
        key: 'contract_name',
        width: 160,
        render: (text) => {
            if (!text) return 'N/A';
            return (
                <AntTooltip title={text} placement="topLeft">
                    <div style={{ 
                        whiteSpace: 'normal', 
                        wordBreak: 'break-word', 
                        lineHeight: '1.4',
                        maxWidth: '100%'
                    }}>
                        {text}
                    </div>
                </AntTooltip>
            );
        },
    },
    // ============================================================
    {
        title: 'Created Aging',
        key: 'created_aging',
        width: 100,
        render: (_, record) => {
            const days = record.days_since_created;
            const color = getAgingColor(days);
            let display = getAgingDisplay(days, null);
            const tooltipText = `Created: ${moment(record.created_at).format('YYYY-MM-DD HH:mm:ss')}`;
            
            if (display !== 'N/A' && display !== '< 1 hour' && !display.includes('Today')) {
                display = `${display} ago`;
            }
            
            return (
                <AntTooltip title={tooltipText}>
                    <Tag color={color} icon={<ClockCircleOutlined />} style={{ whiteSpace: 'nowrap' }}>
                        {display}
                    </Tag>
                </AntTooltip>
            );
        },
    },
    {
        title: 'Pending Signature Type',
        dataIndex: 'signature_type',
        key: 'signature_type',
        width: 190,
        render: (type) => {
            const typeMap = {
                'supervisor_signature': { 
                    text: '🏢 Internal Supervisor Signature', 
                    color: 'blue',
                    description: 'Waiting for Internal/Partner Supervisor to sign'
                },
                'customer_standard_signature': { 
                    text: '👤 Customer Standard Signature', 
                    color: 'orange',
                    description: 'Waiting for Customer Standard user to sign'
                },
                'customer_supervisor_signature': { 
                    text: '👥 Customer Supervisor Signature', 
                    color: 'purple',
                    description: 'Waiting for Customer Supervisor to sign'
                }
            };
            const info = typeMap[type] || { text: type || 'Unknown', color: 'default', description: '' };
            return (
                <AntTooltip title={info.description}>
                    <Tag color={info.color} icon={<SignatureOutlined />} style={{ whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'center' }}>
                        {info.text}
                    </Tag>
                </AntTooltip>
            );
        }
    },
    {
        title: 'Related To',
        key: 'user_info',
        width: 140,
        ellipsis: true,
        render: (_, record) => {
            const name = record.assigned_to_name || record.created_by_name || 'N/A';
            return (
                <AntTooltip title={name} placement="topLeft">
                    <div style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        maxWidth: '130px'
                    }}>
                        {name}
                    </div>
                </AntTooltip>
            );
        }
    },
    {
        title: 'Action',
        key: 'action',
        width: 80,
        fixed: 'right',
        render: (_, record) => (
            <Button 
                type="primary" 
                size="small" 
                icon={<SignatureOutlined />}
                onClick={() => handleSignTicketFromPopup(record.id, record.signature_type)}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', fontSize: '12px' }}
            >
                Sign
            </Button>
        ),
    },
];

// ========== ADD EXPORT FUNCTION HERE ==========
// Export to Word function
const exportToWord = (data, filename) => {
    if (!data || data.length === 0) {
        showError('Export Failed', 'No data to export.');
        return;
    }

    const currentDate = moment().format('DD MMMM YYYY, hh:mm:ss A');
    const companyName = 'YPS Helpdesk System';
    const reportTitle = filename.replace(/_/g, ' ');
    
    const tableRows = data.map((item, index) => {
        const ticketNumber = item.ticket_uuid || 'N/A';
        const title = (item.title || 'N/A').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const contractName = item.contract_name || 'N/A';
        
        let status = 'N/A';
        if (item.signature_type) {
            if (item.signature_type === 'supervisor_signature') {
                status = 'Supervisor Signature';
            } else if (item.signature_type === 'customer_standard_signature') {
                status = 'Customer Standard Signature';
            } else if (item.signature_type === 'customer_supervisor_signature') {
                status = 'Customer Supervisor Signature';
            } else {
                status = item.signature_type;
            }
        } else if (item.status_name) {
            status = item.status_name;
        }
        
        const assignedTo = item.assigned_to_name || 'N/A';
        const created = item.created_at ? moment(item.created_at).format('DD MMM YYYY, hh:mm A') : 'N/A';
        
        let aging = 'N/A';
        if (item.days_in_progress !== undefined && item.days_in_progress !== null) {
            const days = item.days_in_progress;
            if (days === 0) {
                aging = 'Today';
            } else if (days === 1) {
                aging = '1 day';
            } else {
                aging = `${days} days`;
            }
        } else if (item.days_since_created !== undefined && item.days_since_created !== null) {
            const days = item.days_since_created;
            if (days === 0) {
                aging = 'Today';
            } else if (days === 1) {
                aging = '1 day';
            } else {
                aging = `${days} days`;
            }
        } else if (item.created_at) {
            const createdDate = moment(item.created_at);
            const now = moment();
            const days = now.diff(createdDate, 'days');
            if (days === 0) {
                aging = 'Today';
            } else if (days === 1) {
                aging = '1 day';
            } else {
                aging = `${days} days`;
            }
        }
        
        const rowClass = index % 2 === 0 ? 'even-row' : 'odd-row';
        
        return `
            <tr class="${rowClass}">
                <td>${ticketNumber}</td>
                <td class="title-cell">${title}</td>
                <td><span class="status-badge">${status}</span></td>
                <td>${contractName}</td>
                <td>${assignedTo}</td>
                <td>${created}</td>
                <td class="aging-cell">${aging}</td>
            </tr>
        `;
    }).join('');

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${reportTitle} - ${companyName}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Aptos', 'Segoe UI', 'Calibri', 'Arial', sans-serif;
            margin: 0;
            padding: 40px 50px;
            background: white;
            color: #2c3e50;
            line-height: 1.5;
        }
        
        .report-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
        }
        
        .report-header {
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #1a73e8;
        }
        
        .report-title {
            font-size: 28px;
            font-weight: 600;
            color: #1a73e8;
            margin-bottom: 10px;
            letter-spacing: -0.5px;
        }
        
        .company-name {
            font-size: 14px;
            color: #5f6368;
            margin-bottom: 5px;
        }
        
        .info-section {
            background: #f8f9fa;
            padding: 15px 20px;
            margin-bottom: 25px;
            border-radius: 8px;
            border-left: 4px solid #1a73e8;
        }
        
        .info-row {
            display: flex;
            margin-bottom: 8px;
        }
        
        .info-label {
            font-weight: 600;
            width: 120px;
            color: #5f6368;
        }
        
        .info-value {
            color: #202124;
        }
        
        .data-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 13px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .data-table th {
            background: #1a73e8;
            color: white;
            padding: 12px 8px;
            text-align: left;
            font-weight: 600;
            font-size: 12px;
            border: none;
        }
        
        .data-table td {
            padding: 8px;
            border-bottom: 1px solid #e0e0e0;
            vertical-align: top;
            font-size: 12px;
        }
        
        .data-table .even-row {
            background-color: #ffffff;
        }
        
        .data-table .odd-row {
            background-color: #f8f9fa;
        }
        
        .data-table tr:hover {
            background-color: #e8f0fe;
        }
        
        .title-cell {
            font-weight: 500;
            color: #202124;
        }
        
        .aging-cell {
            font-weight: 500;
            text-align: center;
            white-space: nowrap;
        }
        
        .status-badge {
            display: inline-block;
            padding: 3px 8px;
            background: #e8eaed;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            color: #3c4043;
        }
        
        .report-footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e0e0e0;
            text-align: center;
            font-size: 10px;
            color: #9aa0a6;
        }
        
        .disclaimer {
            margin-top: 8px;
            font-style: italic;
        }
        
        /* Balanced Column Widths */
        .data-table th:nth-child(1) { width: 12%; }  /* Ticket Number */
        .data-table th:nth-child(2) { width: 20%; }  /* Title */
        .data-table th:nth-child(3) { width: 14%; }  /* Status */
        .data-table th:nth-child(4) { width: 16%; }  /* Contract */
        .data-table th:nth-child(5) { width: 14%; }  /* Assigned To */
        .data-table th:nth-child(6) { width: 14%; }  /* Created Date */
        .data-table th:nth-child(7) { width: 10%; }  /* Aging */
    </style>
</head>
<body>
    <div class="report-container">
        <div class="report-header">
            <div class="report-title">${reportTitle}</div>
            <div class="company-name">${companyName}</div>
        </div>
        
        <div class="info-section">
            <div class="info-row">
                <div class="info-label">Generated By:</div>
                <div class="info-value">${user?.firstName || 'System'} ${user?.lastName || 'User'} (${user?.roleName || 'User'})</div>
            </div>
            <div class="info-row">
                <div class="info-label">Generated On:</div>
                <div class="info-value">${currentDate}</div>
            </div>
            <div class="info-row">
                <div class="info-label">Total Records:</div>
                <div class="info-value">${data.length} ticket(s)</div>
            </div>
        </div>
        
        <table class="data-table" cellspacing="0">
            <thead>
                <tr>
                    <th>Ticket Number</th>
                    <th>Title / Summary</th>
                    <th>Pending Signature Type</th>
                    <th>Contract</th>
                    <th>Assigned To</th>
                    <th>Created Date</th>
                    <th>Aging</th>
                </tr>
            </thead>
            <tbody>
                ${tableRows}
            </tbody>
        </table>
        
        <div class="report-footer">
            <div>This is an automatically generated report from ${companyName}.</div>
            <div class="disclaimer">* This is a system-generated document. Please do not reply to this email.</div>
        </div>
    </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${filename}_${moment().format('YYYYMMDD_HHmmss')}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showSuccess('Export Successful', `${data.length} tickets exported to Word.`);
};
// ========== END EXPORT FUNCTION ==========

const hasInProgress = inProgressTickets.length > 0;
const hasPendingSignatures = pendingSignatureTickets.length > 0;
const totalPending = inProgressTickets.length + pendingSignatureTickets.length;

    return (
        <ConfigProvider theme={{ algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
            <div className="dashboard-scrollable">
                <div className="dashboard-header">
                    <div className="header-left">
                        <h1>Welcome back, {userName}</h1>
                        <span className="role-badge">{roleName}</span>
                        <p className="header-subtitle">Here's what's happening with your tickets today</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-refresh" onClick={handleRefresh}>
                            <FiRefreshCw /> Refresh Data
                        </button>
                        <button className="btn-primary" onClick={handleCreateTicket}>
                            <FiPlus /> New Ticket
                        </button>
                    </div>
                </div>

                {!dashboardData ? (
                    <div className="dashboard-empty">
                        <p>No dashboard content available.</p>
                    </div>
                ) : (
                    <>
                        {/* Ticket Status Overview Section */}
                        <div className="section-header">
                            <h2>Ticket Status Overview</h2>
                            <button 
                                className={`section-toggle ${showOverviewSection ? 'active' : ''}`} 
                                onClick={toggleOverviewSection}
                            >
                                {showOverviewSection ? <FiEyeOff /> : <FiEye />}
                                {showOverviewSection ? 'Hide Overview' : 'Show Overview'}
                            </button>
                        </div>
                        <div className={`overview-section ${showOverviewSection ? '' : 'hidden'} ${isMobile ? 'mobile' : ''}`}>
                            <div className="cards-grid tier-1">
                                {cardTiers[0].map(card => (
                                    <div 
                                        key={card.type} 
                                        className={`dashboard-card status-card card-${card.size} clickable ${card.isActive ? 'active-view' : ''}`}
                                        style={{ borderLeft: `4px solid ${card.color}` }}
                                        onClick={() => handleCardClick(card.type)}
                                    >
                                        <div className="card-content">
                                            <h3>{card.title}</h3>
                                            <p className="card-value">{card.value}</p>
                                        </div>
                                        <div className="card-icon">
                                            <FiArrowRight className="arrow-icon" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Status Breakdown Section */}
                        <div className="section-header">
                            <h2>
                                Status Breakdown for{' '}
                                {activeView === 'all-tickets' 
                                    ? 'ALL TICKETS' 
                                    : 'MY TICKETS'}
                            </h2>
                            <button 
                                className={`section-toggle ${showBreakdownSection ? 'active' : ''}`} 
                                onClick={toggleBreakdownSection}
                            >
                                {showBreakdownSection ? <FiEyeOff /> : <FiEye />}
                                {showBreakdownSection ? 'Hide Breakdown' : 'Show Breakdown'}
                            </button>
                        </div>
                        <div className={`breakdown-section ${showBreakdownSection ? '' : 'hidden'} ${isMobile ? 'mobile' : ''}`}>
                            <div className="cards-grid tier-2">
                                {cardTiers[1].map(card => (
                                    <div 
                                        key={card.type} 
                                        className={`dashboard-card status-card card-${card.size}`}
                                        style={{ borderLeft: `4px solid ${card.color}` }}
                                    >
                                        <div className="card-content">
                                            <h3>{card.title}</h3>
                                            <p className="card-value">{card.value}</p>
                                        </div>
                                        <div className="card-icon">
                                            <div 
                                                className="status-indicator" 
                                                style={{ backgroundColor: card.color }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="cards-grid tier-3">
                                {cardTiers[2].map(card => (
                                    <div 
                                        key={card.type} 
                                        className={`dashboard-card status-card card-${card.size}`}
                                        style={{ borderLeft: `4px solid ${card.color}` }}
                                    >
                                        <div className="card-content">
                                            <h3>{card.title}</h3>
                                            <p className="card-value">{card.value}</p>
                                        </div>
                                        <div className="card-icon">
                                            <div 
                                                className="status-indicator" 
                                                style={{ backgroundColor: card.color }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Analytics Section */}
                        <div className="section-header">
                            <h2>Analytics Overview</h2>
                            <button 
                                className={`section-toggle ${showAnalyticsSection ? 'active' : ''}`} 
                                onClick={toggleAnalyticsSection}
                            >
                                {showAnalyticsSection ? <FiEyeOff /> : <FiEye />}
                                {showAnalyticsSection ? 'Hide Analytics' : 'Show Analytics'}
                            </button>
                        </div>
                        <div className={`charts-section ${showAnalyticsSection ? '' : 'hidden'} ${isMobile ? 'mobile' : ''}`}>
                            <div className="chart-view-switcher">
                                <button 
                                    className={`chart-view-btn ${chartView === 'all-tickets' ? 'active' : ''}`}
                                    onClick={() => handleChartViewChange('all-tickets')}
                                >
                                    All Tickets
                                </button>
                                <button 
                                    className={`chart-view-btn ${chartView === 'my-tickets' ? 'active' : ''}`}
                                    onClick={() => handleChartViewChange('my-tickets')}
                                >
                                    My Tickets
                                </button>
                            </div>
                            <div className="charts-grid">
                                <div className="chart-container">
                                    <div className="chart-header">
                                        <h3>Ticket Volume (Last 6 Months) - {chartView.replace('-', ' ').toUpperCase()}</h3>
                                        <FiTrendingUp />
                                    </div>
                                    <div className="chart-wrapper">
                                        {lineChartData.labels[0] !== 'No Data' ? (
                                            <Line data={lineChartData} options={chartOptions} />
                                        ) : (
                                            <div className="no-chart-data">
                                                <p>No ticket volume data available for {chartView.replace('-', ' ')}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="chart-container">
                                    <div className="chart-header">
                                        <h3>
                                            Ticket Status Distribution - {chartView.replace('-', ' ').toUpperCase()} 
                                            ({pieChartMode === 'current-month' ? currentMonthYear : 'All Time'})
                                        </h3>
                                        <FiPieChart />
                                    </div>
                                    <div className="pie-mode-switcher">
                                        <button 
                                            className={`pie-mode-btn ${pieChartMode === 'current-month' ? 'active' : ''}`}
                                            onClick={() => handlePieChartModeChange('current-month')}
                                        >
                                            Current Month
                                        </button>
                                        <button 
                                            className={`pie-mode-btn ${pieChartMode === 'all-time' ? 'active' : ''}`}
                                            onClick={() => handlePieChartModeChange('all-time')}
                                        >
                                            All Time
                                        </button>
                                    </div>
                                    <div className="chart-wrapper">
                                        <Pie data={pieChartDataConfig} options={pieChartOptions} />
                                    </div>
                                </div>
                            </div>
                        </div>

{/* Recent Tickets Section */}
<div className="recent-tickets-section">
    <div className="section-header">
        <h2>
            Recent Tickets -{' '}
            {chartView === 'all-tickets' 
                ? 'ALL TICKETS' 
                : 'MY TICKETS'}
        </h2>
        <button className="btn-text" onClick={() => navigate('/tickets')}>
            View More Tickets
        </button>
    </div>
    {recentTickets.length > 0 ? (
        <div className={`table-container ${isMobile ? 'mobile-table' : ''}`}>
            <table className="tickets-table">
                <thead>
                    <tr>
                        <th>Ticket Number</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Assigned To</th>
                        <th>Created At</th>
                    </tr>
                </thead>
                <tbody>
                    {recentTickets.map((ticket) => {
                        // Get assignee name properly
                        const assigneeName = ticket.assignedTo || 'Unassigned';
                        
                        // Get status name properly
                        const statusName = ticket.statusName || ticket.status || 'Unknown';
                        
                        return (
                            <tr key={ticket.id} className="ticket-row">
                                <td data-label="Ticket Number">
                                    <span 
                                        className="ticket-id clickable"
                                        onClick={() => handleTicketClick(ticket.ticketUuid)}
                                    >
                                        {ticket.ticketUuid}
                                    </span>
                                </td>
                                <td data-label="Title" className="ticket-title">
                                    {ticket.title || ticket.subject || 'N/A'}
                                </td>
                                <td data-label="Status">
                                    <span 
                                        className={`status-badge ${statusName.toLowerCase().replace(/\s+/g, '-')}`}
                                        style={{ backgroundColor: statusColorMap[statusName] || '#6b7280' }}
                                    >
                                        {statusName}
                                    </span>
                                </td>
                                <td data-label="Assigned To" className="assigned-to">
                                    <FiUser className="inline-icon" />
                                    {assigneeName}
                                </td>
                                <td data-label="Created At" className="created-at">
                                    <FiCalendar className="inline-icon" />
                                    {formatDate(ticket.createdAt || ticket.caseReportedDate)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    ) : (
        <div className="no-tickets">
            <p>No recent tickets found for {chartView.replace('-', ' ').toLowerCase()}</p>
        </div>
    )}
</div>
                    </>
                )}
            </div>

{/* Pending Actions Popup - Enhanced Version */}
{(hasInProgress || hasPendingSignatures) && (
    <Modal
        title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    background: '#faad14', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                }}>
                    <ExclamationCircleOutlined style={{ color: 'white', fontSize: 20 }} />
                </div>
                <div>
                    <span style={{ fontSize: 18, fontWeight: 600 }}>Pending Actions Required</span>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                        Please review and take action on the following tickets
                    </div>
                </div>
                <Badge count={totalPending} style={{ backgroundColor: '#faad14', marginLeft: 'auto' }} />
            </div>
        }
        open={showPendingPopup}
        onCancel={() => setShowPendingPopup(false)}
        footer={[
            <Button 
                key="close" 
                onClick={() => setShowPendingPopup(false)} 
                size="middle"
                style={{ fontSize: '13px', fontFamily: 'inherit' }}
            >
                Close
            </Button>
        ]}
        width={1300}
        centered
        destroyOnHidden
        styles={{
            body: { padding: '20px 24px' }
        }}
    >
    <Tabs 
        defaultActiveKey={hasPendingSignatures ? "signatures" : "inprogress"} 
        size="large"
        items={[
...(hasPendingSignatures ? [{
    key: 'signatures',
    label: (
        <span>
            <SignatureOutlined style={{ marginRight: 8 }} />
            Pending Signatures
            <Badge count={pendingSignatureTickets.length} style={{ marginLeft: 8, backgroundColor: '#52c41a' }} />
        </span>
    ),
    children: (
        <>
{user?.roleId === 1 ? (
// SYSTEM ADMIN VIEW - Two tables
<>
    {/* Table 1: Tickets Requiring System Admin's Signature */}
    {pendingSignatureTickets.filter(t => t.requires_my_signature).length > 0 && (
        <>
            <div style={{ marginBottom: 16 }}>
                <Alert
                    message={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>Tickets Requiring Your Signature</span>
                            <Button 
                                icon={<FileWordOutlined />} 
                                onClick={() => exportToWord(pendingSignatureTickets.filter(t => t.requires_my_signature), 'My_Signature_Required')}
                                size="small"
                            >
                                Export to Word
                            </Button>
                        </div>
                    }
                    description="These tickets need your signature as Supervisor."
                    type="error"
                    showIcon
                    style={{ borderRadius: 8 }}
                />
            </div>
            <Table
                columns={pendingSignatureSummaryColumns}
                dataSource={pendingSignatureTickets.filter(t => t.requires_my_signature)}
                rowKey="id"
                pagination={false}
                size="middle"
                bordered
                scroll={{ x: 1000 }}
                style={{ marginBottom: 16, borderRadius: 8 }}
            />
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Button 
                    type="primary" 
                    size="large"
                    icon={<SignatureOutlined />}
                    onClick={() => {
                        setShowPendingPopup(false);
                        navigate('/my-tickets?tab=to_sign');
                    }}
                    style={{ 
                        backgroundColor: '#52c41a', 
                        borderColor: '#52c41a',
                        height: 48,
                        padding: '0 32px',
                        fontSize: '16px',
                        fontWeight: 500
                    }}
                >
                    Sign Your Pending Tickets ({pendingSignatureTickets.filter(t => t.requires_my_signature).length})
                </Button>
            </div>
        </>
    )}
    
    {/* Table 2: All Other Pending Signatures (Monitoring) */}
    {pendingSignatureTickets.filter(t => !t.requires_my_signature).length > 0 && (
        <>
            <div style={{ marginBottom: 16 }}>
                <Alert
                    message={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>All Pending Signatures (System Monitoring)</span>
                            <Button 
                                icon={<FileWordOutlined />} 
                                onClick={() => exportToWord(pendingSignatureTickets.filter(t => !t.requires_my_signature), 'All_Pending_Signatures')}
                                size="small"
                            >
                                Export to Word
                            </Button>
                        </div>
                    }
                    description="These tickets are pending signature from other users. For monitoring purposes only."
                    type="info"
                    showIcon
                    style={{ borderRadius: 8 }}
                />
            </div>
            <Table
                columns={pendingSignatureSummaryColumns}
                dataSource={pendingSignatureTickets.filter(t => !t.requires_my_signature)}
                rowKey="id"
                pagination={{ 
                    pageSize: 5, 
                    showSizeChanger: true, 
                    showTotal: (total) => <span style={{ fontSize: '13px' }}>Total {total} tickets</span>,
                    pageSizeOptions: ['5', '10', '20'],
                    size: 'default',
                    showQuickJumper: false
                }}
                size="middle"
                bordered
                scroll={{ x: 1000 }}
                style={{ borderRadius: 8 }}
            />
        </>
    )}
</>
            ) : (
// NON-SYSTEM ADMIN VIEW - One table (current behavior)
<>
    <div style={{ marginBottom: 16 }}>
        <Alert
            message={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>{`${pendingSignatureTickets.length} Ticket(s) Awaiting Your Signature`}</span>
                    <Button 
                        icon={<FileWordOutlined />} 
                        onClick={() => exportToWord(pendingSignatureTickets, 'Pending_Signatures')}
                        size="small"
                        style={{ marginLeft: 'auto' }}
                    >
                        Export to Word
                    </Button>
                </div>
            }
            description="Click the button below to review and sign all pending tickets."
            type="warning"
            showIcon
            style={{ borderRadius: 8 }}
        />
    </div>
    
    <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <Button 
            type="primary" 
            size="large"
            icon={<SignatureOutlined />}
            onClick={() => {
                setShowPendingPopup(false);
                navigate('/my-tickets?tab=to_sign');
            }}
            style={{ 
                backgroundColor: '#52c41a', 
                borderColor: '#52c41a',
                height: 48,
                padding: '0 32px',
                fontSize: '16px',
                fontWeight: 500
            }}
        >
            Go to Tickets to Sign ({pendingSignatureTickets.length})
        </Button>
    </div>
    
    <Table
        columns={pendingSignatureSummaryColumns}
        dataSource={pendingSignatureTickets}
        rowKey="id"
        pagination={{ 
            pageSize: 5, 
            showSizeChanger: true, 
            showTotal: (total) => <span style={{ fontSize: '13px' }}>Total {total} tickets</span>,
            pageSizeOptions: ['5', '10', '20'],
            size: 'default',
            showQuickJumper: false
        }}
        size="middle"
        bordered
        scroll={{ x: 1000 }}
        style={{ borderRadius: 8 }}
    />
</>
            )}
        </>
    )
}] : []),
...(hasInProgress ? [{
    key: 'inprogress',
    label: (
        <span>
            <ClockCircleOutlined style={{ marginRight: 8 }} />
            My Workload
            <Badge count={inProgressTickets.length} style={{ marginLeft: 8, backgroundColor: '#1890ff' }} />
        </span>
    ),
    children: (
        <>
            <div style={{ marginBottom: 16 }}>
                <Alert
                    message={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>My Workload</span>
                            <Button 
                                icon={<FileWordOutlined />} 
                                onClick={() => exportToWord(inProgressTickets, 'My_Workload')}
                                size="small"
                            >
                                Export to Word
                            </Button>
                        </div>
                    }
                    description="Tickets created by you OR assigned to you. Priority increases with age."
                    type="info"
                    showIcon
                    style={{ borderRadius: 8 }}
                />
            </div>
            <Table
                columns={inProgressPopupColumns}
                dataSource={inProgressTickets}
                rowKey="id"
                pagination={{ 
                    pageSize: 5, 
                    showSizeChanger: true, 
                    showTotal: (total) => <span style={{ fontSize: '13px' }}>Total {total} tickets</span>,
                    pageSizeOptions: ['5', '10', '20'],
                    size: 'default',
                    showQuickJumper: false
                }}
                size="middle"
                bordered
                scroll={{ x: 1100 }}
                style={{ borderRadius: 8 }}
            />
        </>
    )
}] : [])
        ]}
    />

    {hasPendingSignatures && (
        <div style={{ 
            marginTop: 20, 
            padding: '12px 16px', 
            background: '#fff1f0', 
            borderRadius: 8, 
            border: '1px solid #ffccc7',
            display: 'flex',
            alignItems: 'center',
            gap: 12
        }}>
            <WarningOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
            <Text type="danger" style={{ flex: 1 }}>
                <strong>Reminder:</strong> Unresolved signature requests may cause processing delays. Please attend to them promptly.
            </Text>
        </div>
    )}
    </Modal>
)}
        </ConfigProvider>
    );
};

export default Dashboard;